import { useState, useCallback, useEffect } from 'react';
import { SocialMediaPostCounts, SocialMediaDailyCount } from '@/lib/api';
import { socialMediaApi } from '../api';

const DEFAULT_COUNTS: SocialMediaPostCounts = {
  total: 0,
  draft: 0,
  scheduled: 0,
  published: 0,
  failed: 0,
  cancelled: 0,
  publishing: 0,
};

/**
 * Fetches aggregate post counts and optional per-day activity for a given month.
 *
 * @param month  Optional YYYY-MM string. When provided the hook re-fetches
 *               whenever the month changes, returning daily activity data
 *               for that month.
 */
export function useSocialPostStats(month?: string) {
  const [counts, setCounts] = useState<SocialMediaPostCounts>(DEFAULT_COUNTS);
  const [dailyActivity, setDailyActivity] = useState<SocialMediaDailyCount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadStats = useCallback(async (targetMonth?: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await socialMediaApi.getSocialMediaStats(targetMonth);
      setCounts(result.counts);
      setDailyActivity(result.dailyActivity);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load stats');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats(month);
  }, [loadStats, month]);

  return { counts, dailyActivity, isLoading, error, loadStats };
}
